import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";

import { RootState } from "app/store";
import {
  decodeToken,
  isTokenExpired,
  removeSunoCommsCachedAuthData
} from "services/suno-comms/utils/auth.utils";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import { QUICK_FILTER_ALL_LEADS_TYPE } from "./components/statistics-panel/constants";

interface LeadsState {
  showLeadFormMode: "new" | "edit" | "view" | undefined;
  currentLeadId?: string | null;
  showSentEmails: boolean;
  access_token?: string;
  refresh_token?: string;
  access_token_expires_at?: string;
  customer_id?: string;
  quickFilterCode: string;
}

const showEmailsStorage = setupLocalStorageAPI("suno_comms_show_emails");
const showTokenStorage = setupLocalStorageAPI("suno_comms_token");
const showRefreshToken = setupLocalStorageAPI("suno_comms_refresh_token");
const showTokenExpiration = setupLocalStorageAPI("suno_comms_token_expiration");

const initialState: LeadsState = {
  showLeadFormMode: undefined,
  showSentEmails: showEmailsStorage.getData(true),
  access_token: showTokenStorage.getData(undefined) || undefined,
  refresh_token: showRefreshToken.getData(undefined) || undefined,
  access_token_expires_at: showTokenExpiration.getData(undefined) || undefined,
  customer_id: undefined,
  quickFilterCode: QUICK_FILTER_ALL_LEADS_TYPE
};

export const leadsSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setShowLeadForm: (
      state,
      action: PayloadAction<{
        mode: "new" | "view" | "edit" | undefined;
        leadId?: string | null;
      }>
    ) => {
      const { mode, leadId } = action.payload;
      state.showLeadFormMode = mode;
      state.currentLeadId = leadId;
    },
    setShowSentEmails: (state, action: PayloadAction<boolean>) => {
      state.showSentEmails = action.payload;
      showEmailsStorage.updateData(state.showSentEmails);
    },
    logoutSunoComms: (state) => {
      removeSunoCommsCachedAuthData();
      state.access_token = undefined;
      state.refresh_token = undefined;
      state.access_token_expires_at = undefined;
      state.customer_id = undefined;
    },
    setAuthTokenInfo: (
      state,
      action: PayloadAction<{
        access_token?: string;
        refresh_token?: string;
        access_token_expires_at?: string;
      }>
    ) => {
      const { access_token, refresh_token, access_token_expires_at } =
        action.payload;

      state.access_token = access_token;
      state.refresh_token = refresh_token;
      state.access_token_expires_at = access_token_expires_at;

      // Extract customer_id from the JWT token
      if (access_token) {
        const decoded = decodeToken(access_token);
        state.customer_id = decoded?.customer_id;
      }

      localStorage.setItem("suno_comms_token", access_token || "");
      localStorage.setItem("suno_comms_refresh_token", refresh_token || "");
      localStorage.setItem(
        "suno_comms_token_expiration",
        access_token_expires_at || ""
      );
    },
    applyQuickFilter: (state, action: PayloadAction<string>) => {
      const { payload } = action;
      state.quickFilterCode = payload;
    }
  }
});

export const {
  setShowLeadForm,
  setShowSentEmails,
  setAuthTokenInfo,
  logoutSunoComms,
  applyQuickFilter
} = leadsSlice.actions;

export default leadsSlice.reducer;

const leadsSelector = (state: RootState) => state.leads;

export const selectShowLeadFormMode = createSelector(
  leadsSelector,
  (leads) => leads.showLeadFormMode
);

export const selectCurrentLeadId = createSelector(
  leadsSelector,
  (leads) => leads.currentLeadId
);

export const selectShowSentEmails = createSelector(
  leadsSelector,
  (leads) => leads.showSentEmails
);

export const selectQuickFilterCode = createSelector(
  leadsSelector,
  (leads) => leads.quickFilterCode
);

export const selectCommsAuthorized = createSelector(
  leadsSelector,
  (leads) =>
    leads.access_token &&
    leads.access_token_expires_at &&
    !isTokenExpired(leads.access_token_expires_at)
);

export const selectCommsCustomerId = createSelector(
  leadsSelector,
  (leads) => leads.customer_id
);
